import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

interface ExpandableTextProps {
  text?: string | null;
  lines?: number;
  className?: string;
}

export default function ExpandableText({
  text,
  lines = 2,
  className = "",
}: ExpandableTextProps) {
  const { t } = useTranslation();
  const measureRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState(0);
  const [expandedHeight, setExpandedHeight] = useState(0);

  useLayoutEffect(() => {
    setExpanded(false);
  }, [text]);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el || !text) {
      setIsOverflowing(false);
      return;
    }

    const updateOverflow = () => {
      const styles = getComputedStyle(el);
      const fontSize = parseFloat(styles.fontSize);
      const parsedLineHeight = parseFloat(styles.lineHeight);
      const lineHeight = Number.isNaN(parsedLineHeight)
        ? fontSize * 1.25
        : parsedLineHeight;

      const nextCollapsedHeight = lineHeight * lines;
      const nextExpandedHeight = el.scrollHeight;

      setCollapsedHeight(nextCollapsedHeight);
      setExpandedHeight(nextExpandedHeight);
      setIsOverflowing(nextExpandedHeight > nextCollapsedHeight + 1);
    };

    updateOverflow();

    const observer = new ResizeObserver(updateOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, lines]);

  if (!text) return null;

  const measured = collapsedHeight > 0 && expandedHeight > 0;

  return (
    <div className={`relative ${className}`}>
      <div
        ref={measureRef}
        aria-hidden
        className="invisible pointer-events-none absolute h-0 overflow-hidden whitespace-pre-line w-full"
      >
        {text}
      </div>
      <motion.div
        initial={false}
        animate={
          measured
            ? {
                height:
                  expanded || !isOverflowing
                    ? expandedHeight
                    : collapsedHeight,
              }
            : undefined
        }
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="overflow-hidden"
      >
        <div
          className={`whitespace-pre-line ${!measured && !expanded ? "line-clamp-2" : ""}`}
        >
          {text}
        </div>
      </motion.div>
      {isOverflowing && (
        <div className="text-right">
          <button
            type="button"
            className="underline cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              setExpanded((current) => !current);
            }}
          >
            {expanded
              ? t("scenes.accommodation.show_less")
              : t("scenes.accommodation.show_more")}
          </button>
        </div>
      )}
    </div>
  );
}
